/**
 * useDocuments — react-query hooks for the @kgc/documents backend.
 *
 * Single file keeps list + upload + delete + audit together so the
 * DocumentsPanel and DocumentsAuditPage can import from one place.
 *
 * Query keys:
 *   ['documents', 'list', sourceType, sourceId] — scoped per source
 *   ['documents', 'audit', query]                — admin audit page
 *
 * All mutations invalidate the matching list query on success; errors
 * surface as `toast.error(t('documents.errors.*'))` at the caller level.
 */

import {
  deleteDocument,
  downloadDocument,
  listDocumentAudit,
  listDocuments,
  uploadDocument,
  type DocumentAuditQuery,
  type DocumentAuditResponse,
  type DocumentListItem,
  type DocumentSourceType,
  type UploadDocumentInput,
  type UploadedDocument,
} from '@/api/documents';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ═════════════════════════════════════════════════════════════════════════
// Query keys — single source of truth for cache invalidation
// ═════════════════════════════════════════════════════════════════════════

export const documentsQueryKeys = {
  all: ['documents'] as const,
  list: (sourceType: DocumentSourceType, sourceId: string) =>
    ['documents', 'list', sourceType, sourceId] as const,
  audit: (query: DocumentAuditQuery) => ['documents', 'audit', query] as const,
};

// ═════════════════════════════════════════════════════════════════════════
// Queries
// ═════════════════════════════════════════════════════════════════════════

/**
 * Fetch the list of documents attached to a given source entity. Call
 * from any detail page (delivery note, partner, vehicle, etc.).
 */
export function useDocumentsList(
  sourceType: DocumentSourceType | undefined,
  sourceId: string | undefined
) {
  return useQuery<DocumentListItem[]>({
    queryKey:
      sourceType !== undefined && sourceId !== undefined
        ? documentsQueryKeys.list(sourceType, sourceId)
        : ['documents', 'list', 'idle'],
    queryFn: async () => {
      if (sourceType === undefined || sourceId === undefined) return [];
      const res = await listDocuments(sourceType, sourceId);
      return res.data;
    },
    enabled: sourceType !== undefined && sourceId !== undefined,
    // Documents change rarely — 30s stale window is ample for a live panel.
    staleTime: 30_000,
  });
}

/**
 * Admin audit log query. Pagination params are part of the query key so
 * react-query caches each page separately.
 */
export function useDocumentsAudit(query: DocumentAuditQuery, enabled = true) {
  return useQuery<DocumentAuditResponse>({
    queryKey: documentsQueryKeys.audit(query),
    queryFn: () => listDocumentAudit(query),
    enabled,
    // Audit rows are immutable once written, so we can hold them longer.
    staleTime: 60_000,
  });
}

// ═════════════════════════════════════════════════════════════════════════
// Mutations
// ═════════════════════════════════════════════════════════════════════════

export function useUploadDocument() {
  const queryClient = useQueryClient();
  return useMutation<UploadedDocument, Error, UploadDocumentInput>({
    mutationFn: async input => {
      const res = await uploadDocument(input);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: documentsQueryKeys.list(variables.sourceType, variables.sourceId),
      });
    },
  });
}

export function useDeleteDocument(sourceType: DocumentSourceType, sourceId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await deleteDocument(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: documentsQueryKeys.list(sourceType, sourceId),
      });
    },
  });
}

/**
 * Download a document in-place (not a mutation; kept here for symmetry so
 * callers can `useDownloadDocument()` alongside other hooks).
 *
 * Returns a stable invoker function rather than a mutation object —
 * downloads are fire-and-forget and react-query caching would not help.
 */
export function useDownloadDocument() {
  return async (id: string, fileName: string): Promise<void> => {
    await downloadDocument(id, fileName);
  };
}
